import { supabase } from '@/lib/supabase';

export type WaLoggingSettings = {
  organization_id: string;
  enabled: boolean;
  log_media: boolean;
  log_groups: boolean;
  enabled_by: string | null;
  enabled_at: string | null;
  disabled_at: string | null;
  updated_at: string | null;
};

export type WaConversation = {
  id: string;
  contact_id: string | null;
  jid: string | null;
  phone: string | null;
  display_name: string | null;
  group_subject: string | null;
  is_group: boolean;
  last_message_preview: string | null;
  last_message_at: string | null;
  message_count: number;
};

/** Columns of public.wa_messages (admin-wa-log select *). */
export type WaMessage = {
  id: string;
  organization_id: string;
  conversation_id: string;
  device_id: string | null;
  contact_id: string | null;
  wa_message_id: string | null;
  direction: 'in' | 'out';
  sender_jid: string | null;
  body: string | null;
  message_type: string;
  media_r2_key: string | null;
  media_mime: string | null;
  media_size_bytes: number | null;
  media_filename: string | null;
  reply_to_wa_message_id: string | null;
  status: string | null;
  error_message: string | null;
  sent_by_user_id: string | null;
  timestamp: string | null;
  created_at: string | null;
  sender_name: string | null;
  is_backfill: boolean | null;
  processed_at: string | null;
  source: string | null;
};

type SettingsResponse = {
  settings: WaLoggingSettings;
  counts: { messages: number; conversations: number };
};

type ConversationsResponse = {
  conversations: WaConversation[];
  page: number;
  pageSize: number;
  total: number;
};

type MessagesResponse = {
  messages: WaMessage[];
  page: number;
  pageSize: number;
  total: number;
};

type SetSettingsInput = {
  enabled?: boolean;
  logMedia?: boolean;
  logGroups?: boolean;
};

class WaLogApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'WaLogApiError';
    this.code = code;
    this.status = status;
  }
}

function mapError(status: number, body: { error?: string } | null): WaLogApiError {
  const code = body?.error ?? 'unknown';
  if (status === 403 || code === 'forbidden') {
    return new WaLogApiError('אין הרשאת אדמין', 'forbidden', 403);
  }
  if (status === 500 && code === 'admin_not_configured') {
    return new WaLogApiError(
      'משתנה OTOMATOR_ADMIN_EMAIL לא מוגדר בפרויקט',
      'admin_not_configured',
      500,
    );
  }
  return new WaLogApiError(
    body?.error ? `שגיאה: ${body.error}` : 'שגיאה בטעינת תיעוד השיחות',
    code,
    status || 500,
  );
}

async function callAdminWaLog<T>(payload: Record<string, unknown>): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new WaLogApiError('לא מחובר — יש להתחבר מחדש', 'unauthenticated', 401);
  }

  // Call Jumpstart project edge function with the user's JWT (same project as admin auth).
  // Never query wa_* tables directly — RLS returns only the caller's own org.
  const { data, error } = await supabase.functions.invoke('admin-wa-log', {
    body: payload,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  });

  if (error) {
    // functions.invoke surfaces non-2xx as FunctionsHttpError; try body from context.
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = (await ctx.json()) as { error?: string };
        throw mapError(ctx.status, body);
      } catch (e) {
        if (e instanceof WaLogApiError) throw e;
      }
    }
    const msg = error.message ?? '';
    if (/forbidden|403/i.test(msg)) {
      throw new WaLogApiError('אין הרשאת אדמין', 'forbidden', 403);
    }
    if (/admin_not_configured/i.test(msg)) {
      throw new WaLogApiError(
        'משתנה OTOMATOR_ADMIN_EMAIL לא מוגדר בפרויקט',
        'admin_not_configured',
        500,
      );
    }
    throw new WaLogApiError(msg || 'שגיאה בטעינת תיעוד השיחות', 'invoke_error', 500);
  }

  if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
    const code = (data as { error: string }).error;
    if (code === 'forbidden') throw mapError(403, data as { error: string });
    if (code === 'admin_not_configured') throw mapError(500, data as { error: string });
    throw mapError(400, data as { error: string });
  }

  return data as T;
}

export const waLogApi = {
  getSettings: (orgId: string) =>
    callAdminWaLog<SettingsResponse>({
      action: 'get_settings',
      organizationId: orgId,
    }),

  setSettings: (orgId: string, patch: SetSettingsInput) =>
    callAdminWaLog<{ settings: WaLoggingSettings }>({
      action: 'set_settings',
      organizationId: orgId,
      enabled: patch.enabled,
      logMedia: patch.logMedia,
      logGroups: patch.logGroups,
    }),

  listConversations: (orgId: string, page: number, pageSize: number) =>
    callAdminWaLog<ConversationsResponse>({
      action: 'list_conversations',
      organizationId: orgId,
      page,
      pageSize,
    }),

  getThread: (orgId: string, conversationId: string, page: number, pageSize: number) =>
    callAdminWaLog<MessagesResponse>({
      action: 'get_thread',
      organizationId: orgId,
      conversationId,
      page,
      pageSize,
    }),

  search: (orgId: string, query: string, page: number, pageSize: number) =>
    callAdminWaLog<MessagesResponse>({
      action: 'search',
      organizationId: orgId,
      query,
      page,
      pageSize,
    }),
};

export { WaLogApiError };
